import linkify, { Match } from 'linkify-it';
import React, { ReactNode, useMemo } from 'react';
import styled, { css, CSSProp } from 'styled-components';

import { blue } from '@vimeo/iris/color';

import hasPort from '../libs/hasPort';

const isIpad =
  typeof window !== 'undefined' &&
  (/ipad/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

/**
 * Generates a linkify object.
 */
const validate = () => false;
const linkifyObj = new linkify(
  {
    'ftp:': { validate },
    'mailto:': { validate },
  },
  { fuzzyIP: false, fuzzyEmail: false },
);

interface TextSplitterProps {
  text: string;
  paraBreak?: number;
}

/**
 * If the device is iPad, the text is split into a new span when
 * the character count is more than 400 characters. If the device
 * is not iPad, the text is not split and is rendered as it is.
 */
const TextSplitter = ({ text, paraBreak }: TextSplitterProps) => {
  const memoized = useMemo(() => {
    /**
     * Note : IPad has rendering issue for long paragraph, here we are splitting paragraph and
     * pushing CSS animations into hardware acceleration to solve this issue.
     */
    if (isIpad && paraBreak && text.length > paraBreak) {
      const splitPara = text.match(new RegExp(`.{1,${paraBreak}}`, 'g')) || [];
      return (
        <>
          {splitPara.map((innerText, innerIndex) => (
            <Span key={innerIndex}>{innerText}</Span>
          ))}
        </>
      );
    }
    return <>{text}</>;
  }, [text]);
  return <>{memoized}</>;
};

interface SupplementalLinkifyLinkProps {
  customLinkCss?: CSSProp;
  paraBreak?: number;
  linkWordBreakStyle?: string;
}

interface LinkifyLinkProps extends Match, SupplementalLinkifyLinkProps {}

/**
 * Converts the URL into an anchor element if it doesn't contain a port.
 */
const LinkifyLink = ({ raw, url, customLinkCss, paraBreak, ...props }: LinkifyLinkProps) => {
  const memoized = useMemo(() => {
    if (hasPort(url)) {
      return <TextSplitter text={raw} paraBreak={paraBreak} />;
    }
    return (
      <CustomLinkText
        contentEditable={false}
        href={url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        {...props}
      >
        {raw}
      </CustomLinkText>
    );
  }, [raw, url]);
  return <>{memoized}</>;
};

interface LinkifyProps extends SupplementalLinkifyLinkProps {
  text: string;
  onMouseEnter?: (event: MouseEvent) => void;
  onMouseLeave?: (event: MouseEvent) => void;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

/**
 * Converts URLs in text to anchor tags from plain text.
 * Converts fuzzy links or links starting with
 * * http://
 * * https://
 * * //
 *
 * mailto: and ftp: are not converted into links
 */
const Linkify = ({ text, ...props }: LinkifyProps) => {
  const memoized = useMemo(() => {
    if (linkifyObj.pretest(text)) {
      let lastIndex = 0;
      const updatedFrags: ReactNode[] = [];
      const urlFragments = linkifyObj.match(text) || [];
      urlFragments.forEach((fragment) => {
        updatedFrags.push(
          <TextSplitter
            key={`text-splitter-${fragment.index}`}
            text={text.substring(lastIndex, fragment.index)}
          />,
        );
        updatedFrags.push(
          <LinkifyLink key={`linkify-link-${fragment.index}`} {...fragment} {...props} />,
        );
        lastIndex = fragment.lastIndex;
      });
      updatedFrags.push(
        <TextSplitter key={`text-splitter-${lastIndex}`} text={text.substring(lastIndex)} />,
      );
      return updatedFrags;
    }
    return <TextSplitter text={text} />;
  }, [text]);

  return <>{memoized}</>;
};

const linkTextCss = css`
  text-decoration: none;
  color: ${blue(500)};
  &:hover {
    color: ${blue(600)};
  }
`;

const CSSForFfFlickering = css`
  transform: translate3d(0, 0, 0);
  transform-style: preserve-3d;
  backface-visibility: hidden;
  position: relative;
`;

const Span = styled.span`
  ${CSSForFfFlickering}
`;

const CustomLinkText = styled.a<{ linkWordBreakStyle?: string; customLinkCss?: CSSProp }>`
  ${CSSForFfFlickering}
  word-break: ${({ linkWordBreakStyle }) => linkWordBreakStyle || 'break-all'};
  ${({ customLinkCss }) => (customLinkCss ? customLinkCss : linkTextCss)}
`;

export default Linkify;
